import type { NearbyCenter } from "./hooks/useNearbyCenter";

const LOCATION_KEYS = ["lat", "lng", "location_label", "radius_km"] as const;

function coordinate(
  value: string | null,
  minimum: number,
  maximum: number,
): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum
    ? parsed
    : null;
}

/** Read only a complete, bounded location pair from a shareable discovery URL. */
export function readDiscoveryCenter(
  params: URLSearchParams,
): NearbyCenter | null {
  const latitude = coordinate(params.get("lat"), -90, 90);
  const longitude = coordinate(params.get("lng"), -180, 180);
  if (latitude === null || longitude === null) return null;
  const label = params.get("location_label")?.trim() || undefined;
  // URL coordinates are shareable and may enter history/referrers. Accept only
  // an explicitly labelled coarse area; exact device fixes stay client-side.
  if (!label) return null;
  return { latitude, longitude, label, coarse: true };
}

/** Replace a URL's location atomically so partial coordinates never survive. */
export function writeDiscoveryCenter(
  params: URLSearchParams,
  center: NearbyCenter,
  radiusKm: number,
): URLSearchParams {
  if (center.coarse !== true || !center.label?.trim()) {
    return clearDiscoveryCenter(params);
  }
  const next = new URLSearchParams(params);
  next.set("lat", String(center.latitude));
  next.set("lng", String(center.longitude));
  next.set("location_label", center.label.trim());
  next.set("radius_km", String(radiusKm));
  next.delete("page");
  next.delete("offset");
  return next;
}

export function clearDiscoveryCenter(
  params: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of LOCATION_KEYS) next.delete(key);
  next.delete("page");
  next.delete("offset");
  return next;
}
